import React from 'react';
import { Link } from 'react-router-dom';
import { Terminal, ArrowLeft, ArrowRight, Boxes, Download, BookOpen, PlayCircle, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CodeBox, Callout, useDocMeta } from '../components/esl/EslShared';

const CliPage = () => {
  useDocMeta(
    'BLOX CLI | Herramienta derivada de BLOX Core',
    'BLOX CLI es la herramienta derivada de BLOX Core para ejecutar, probar y depurar scripts .blox desde terminal. Mismo lenguaje, mismo runtime, sin host embebido.'
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-28 pb-16 border-b border-slate-800/60">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6">
          <Link
            to="/embedded-scripting-language"
            data-testid="cli-back-link"
            className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            BLOX Core es el producto principal
          </Link>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium mb-6">
            <Terminal className="w-3 h-3" />
            Herramienta derivada
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight">
            BLOX <span className="text-emerald-400">CLI</span>
          </h1>

          <p className="text-lg sm:text-2xl text-slate-300 mb-6 max-w-3xl leading-relaxed">
            Ejecuta programas <code className="font-mono text-emerald-300">.blox</code> directamente desde la terminal usando el mismo lenguaje y runtime que <Link to="/embedded-scripting-language" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-4">BLOX Core</Link>.
          </p>

          <Callout tone="cyan" label="Relación con BLOX Core">
            BLOX CLI <strong>no es el producto principal</strong>. Es una herramienta construida sobre BLOX Core para aprender, prototipar, automatizar tareas y validar scripts <em>antes</em> de integrarlos en una aplicación nativa. El producto principal es <Link to="/embedded-scripting-language" className="text-cyan-300 underline underline-offset-2">BLOX Core</Link>, la VM embebible con API C.
          </Callout>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/download"
              data-testid="cli-cta-download"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors"
            >
              <Download className="w-4 h-4" />
              Descargar BLOX CLI
            </Link>
            <Link
              to="/docs"
              data-testid="cli-cta-docs"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Documentación del lenguaje
            </Link>
          </div>
        </div>
      </section>

      {/* USO BÁSICO */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Uso básico</h2>
          <p className="text-slate-400 text-lg mb-8 max-w-3xl leading-relaxed">
            La CLI ejecuta un archivo <code className="font-mono text-emerald-300">.blox</code> directamente. No necesita host, no necesita VM manual: es un envoltorio conveniente sobre BLOX Core.
          </p>

          <CodeBox filename="terminal" lang="powershell">{`# Ejecutar un script
blox script.blox

# Ver ayuda
blox --help

# Ver versión
blox --version`}</CodeBox>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-xl bg-slate-800/40 border border-slate-700/60">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
                <BookOpen className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="text-white font-semibold text-base mb-2">Aprender</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Escribe scripts <code className="font-mono text-emerald-300">.blox</code> y ejecútalos al instante. Ideal para clases, docentes y estudiantes.</p>
            </div>
            <div className="p-5 rounded-xl bg-slate-800/40 border border-slate-700/60">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
                <PlayCircle className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="text-white font-semibold text-base mb-2">Prototipar reglas</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Valida la lógica de negocio o industrial desde terminal antes de embeberla en el host C/C++.</p>
            </div>
            <div className="p-5 rounded-xl bg-slate-800/40 border border-slate-700/60">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
                <Zap className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="text-white font-semibold text-base mb-2">Automatizar</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Corre utilidades locales, valida datos, genera reportes. Todo con el mismo lenguaje del Core.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MISMO LENGUAJE, DOS ENTORNOS */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Mismo lenguaje, dos entornos</h2>
          <p className="text-slate-400 text-lg mb-8 max-w-3xl leading-relaxed">
            El script <code className="font-mono text-emerald-300">.blox</code> que corres desde la CLI es el mismo que un host C puede cargar en BLOX Core. Cambia el envoltorio, no el lenguaje.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Terminal className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-semibold">Ejecutado desde CLI</span>
              </div>
              <CodeBox filename="hola.blox · $ blox hola.blox" lang="blox">{`FUNCION PRINCIPAL
INICIO
    IMPRIMIR("Hola desde la CLI")
FINAL`}</CodeBox>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Boxes className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-400 font-semibold">Embebido en un host C</span>
              </div>
              <CodeBox filename="host.c · blox_vm_load_file(...)" lang="c">{`BloxVM *vm = blox_vm_create();
blox_vm_load_file(vm, "hola.blox");
blox_vm_run_main(vm);
blox_vm_destroy(vm);`}</CodeBox>
            </div>
          </div>

          <Callout tone="cyan" label="Consejo">
            Cuando una regla ya funciona bien desde la CLI, moverla al host embebido solo requiere cargar el mismo archivo <code className="font-mono text-cyan-300">.blox</code> con <code className="font-mono text-cyan-300">blox_vm_load_file()</code>. La lógica no cambia.
          </Callout>
        </div>
      </section>

      {/* CTA */}
      <section className="pt-16 pb-32 sm:pb-24 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Boxes className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            ¿Listo para embeber BLOX en tu aplicación?
          </h2>
          <p className="text-slate-400 text-base mb-8 max-w-2xl mx-auto">
            BLOX CLI te sirvió para prototipar. Ahora integra el mismo script en tu host C, C++, Godot o firmware ESP32-S3 con BLOX Core.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/embedded-scripting-language"
              data-testid="cli-to-core"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-cyan-500 text-slate-950 font-semibold hover:bg-cyan-400 transition-colors"
            >
              <Boxes className="w-4 h-4" />
              Ir a BLOX Core
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CliPage;
